import { race, take } from 'redux-saga/effects';

import { type GeneratorResult, OperationStatus, select, selectTyped, subscribe } from '@pressreader/appstore';
import { toBool } from '@pressreader/types';

import { configLoadDone, configLoadFailed, selectGetConfigValue, selectStatus } from './store';

export function configLoaded() {
    const status = select(selectStatus);
    if (status === OperationStatus.Done) {
        return Promise.resolve();
    }
    if (status === OperationStatus.Failed) {
        return Promise.reject(new Error('Load config failed.'));
    }
    return new Promise<void>((resolve, reject) => {
        // We have to check this again since Promise body is called async and store's content could've changed.
        const status = select(selectStatus);
        if (status === OperationStatus.Done) {
            resolve();
        } else if (status === OperationStatus.Failed) {
            reject(new Error('Load config failed.'));
        } else {
            const unsubscribe = subscribe(selectStatus, s => {
                if (s === OperationStatus.Done) {
                    resolve();
                } else if (s === OperationStatus.Failed) {
                    reject(new Error('Load config failed.'));
                } else {
                    return;
                }
                unsubscribe();
            });
        }
    });
}

function getConfigValue<T>(path: string, defaultValue: T): T;
function getConfigValue<T>(path: string): T | undefined;
function getConfigValue<T>(path: string, defaultValue?: T) {
    return select(selectGetConfigValue)(path, defaultValue);
}

function getConfigValueAsync<T>(path: string, defaultValue: T): Promise<T>;
function getConfigValueAsync<T>(path: string): Promise<T | undefined>;
async function getConfigValueAsync<T>(path: string, defaultValue?: T) {
    await configLoaded();
    return getConfigValue(path, defaultValue);
}

/**
 * Imperative counterpart to `selectGetConfigBoolValue`, mirroring `getConfigValue`. It reads the
 * store on each call, so use it outside selectors only.
 */
function getConfigBoolValue(path: string, defaultValue = false): boolean {
    return toBool(getConfigValue<unknown>(path, defaultValue));
}

/**
 * Awaits the initial config load, then reads the flag through {@link getConfigBoolValue}. Use it
 * from code that can run before the config has arrived, such as app initialisation. Rejects if the
 * config failed to load, exactly as {@link getConfigValueAsync} does.
 */
async function getConfigBoolValueAsync(path: string, defaultValue = false): Promise<boolean> {
    await configLoaded();
    return getConfigBoolValue(path, defaultValue);
}

/**
 * Saga-flavoured counterpart to {@link configLoaded}: returns once the config has
 * finished its initial load, yielding the current `selectStatus` value. If the
 * load is already settled the helper returns synchronously; otherwise it waits
 * for either `configLoadDone` or `configLoadFailed` to fire, then re-reads
 * `selectStatus` so the helper's return remains the single source of truth
 * carried by the reducer.
 *
 * Note: the config reducer currently collapses both success and failure into
 * `OperationStatus.Done` (see `store/reducer.ts`), so callers will observe
 * `Done` for both outcomes today. A future fix that propagates `Failed`
 * through the reducer will surface through this helper without any change
 * here.
 */
export function* getConfigLoadStatus(): GeneratorResult<OperationStatus> {
    const initialStatus = yield* selectTyped(selectStatus);
    if (initialStatus === OperationStatus.Done || initialStatus === OperationStatus.Failed) {
        return initialStatus;
    }
    yield race({
        done: take(configLoadDone),
        failed: take(configLoadFailed),
    });
    return yield* selectTyped(selectStatus);
}

/**
 * Run `register` once when a config-derived boolean predicate becomes true.
 *
 * - If `selectEnabled(state)` is already true → calls `register()` synchronously;
 * - else subscribes to the store and calls `register()` on the next change that
 *   flips the predicate true (e.g. config reload after `svcAuth.tokenUpdated`,
 *   when the server delivers an auth-gated feature flag).
 *
 * Subscription unsubscribes after `register()` fires. At most one call per
 * invocation.
 *
 * Caller responsibility: invoke downstream of `await initAppCore()` so config
 * is already loaded. If a mid-bootstrap reload races (e.g. cached-JWT
 * `tokenUpdated` between `initAppCore` resolving and this call), the
 * subscribe path catches the flip — `state.config` retains the prior payload
 * during the reload, so a stale `false` read just defers registration to the
 * `configLoadDone`-triggered flip.
 */
function registerWhenEnabled<S = unknown>(selectEnabled: (state: S) => boolean, register: () => void): void {
    if (select(selectEnabled)) {
        register();
        return;
    }

    const unsubscribe = subscribe(selectEnabled, enabled => {
        if (enabled) {
            unsubscribe();
            register();
        }
    });
}

export { getConfigBoolValue, getConfigBoolValueAsync, getConfigValue, getConfigValueAsync, registerWhenEnabled };
